import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Context } from '@/types';
import { useAppContext } from './AppContextProvider';

/**
 * F3 — the platform console's notion of "which group are you working on".
 *
 * A platform administrator can hold GROUP contexts (identities phase): seats at
 * a group that several institutions share, reported by identity with a groupId
 * and a null institutionId. Those contexts arrive in `availableContexts` like
 * every other seat, but nothing in the institution world can do anything with
 * them — there is no institution to scope to — so the console surfaces them
 * here, as an optional working scope.
 *
 * The scope is a *working* scope, not an authorisation: choosing a group never
 * gates anything, every endpoint still verifies the operator's token, and the
 * console's own scope bar keeps stating what the operator is looking at. It is
 * also deliberately optional — an operator with group contexts can still work
 * platform-wide, exactly as they could before the console had groups.
 *
 * The choice survives navigation (and reloads) in sessionStorage, so opening
 * the Administrators tab after landing on the group page does not forget which
 * group was picked.
 */
const STORAGE_KEY = 'campusone.platform.group-scope';

interface GroupScopeValue {
  /** The operator's group contexts, in the order identity sent them. */
  groupContexts: Context[];
  /** The selected group's id, or null when working platform-wide. */
  activeGroupId: string | null;
  /** The selected group context, or null when none is selected (or held). */
  activeGroup: Context | null;
  setActiveGroupId: (groupId: string | null) => void;
}

const GroupScopeContext = createContext<GroupScopeValue | null>(null);

export function GroupScopeProvider({ children }: { children: ReactNode }) {
  const { availableContexts } = useAppContext();
  const [activeGroupId, setActiveGroupIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const groupContexts = useMemo(
    () => availableContexts.filter((ctx) => typeof ctx.groupId === 'string'),
    [availableContexts],
  );

  const activeGroup = useMemo(
    () => groupContexts.find((ctx) => ctx.groupId === activeGroupId) ?? null,
    [groupContexts, activeGroupId],
  );

  const setActiveGroupId = useCallback((groupId: string | null) => {
    setActiveGroupIdState(groupId);
    try {
      if (groupId === null) {
        sessionStorage.removeItem(STORAGE_KEY);
      } else {
        sessionStorage.setItem(STORAGE_KEY, groupId);
      }
    } catch {
      // Storage unavailable (private mode, quota): the choice just does not
      // survive a reload, which is a minor annoyance, not a failure.
    }
  }, []);

  // A persisted scope pointing at a group the operator no longer holds must not
  // pretend to be live. Drop it silently rather than showing a scope bar that
  // names a group the operator cannot actually see.
  useEffect(() => {
    if (activeGroupId && !activeGroup) {
      setActiveGroupId(null);
    }
  }, [activeGroupId, activeGroup, setActiveGroupId]);

  return (
    <GroupScopeContext.Provider value={{ groupContexts, activeGroupId, activeGroup, setActiveGroupId }}>
      {children}
    </GroupScopeContext.Provider>
  );
}

export function useGroupScope() {
  const ctx = useContext(GroupScopeContext);
  if (!ctx) throw new Error('useGroupScope must be used within GroupScopeProvider');
  return ctx;
}
